import QueryKeys from '@src/constants/queryKeys';
import useCreateBulkDevices from '@src/hooks/api/mutations/useCreateBulkDevices';
import useGetAllDevicesByType from '@src/hooks/api/queries/devices/useGetAllDevicesByType';
import useGetDevices from '@src/hooks/api/queries/devices/useGetDevices';
import useGetDevicesParentControllers from '@src/hooks/api/queries/devices/useGetDevicesParentControllers';
import useGetParentDevices from '@src/hooks/api/queries/devices/useGetDevicesParents';
import useGetDeviceTypes from '@src/hooks/api/queries/devices/useGetDeviceTypes';
import useGetUnitControllers from '@src/hooks/api/queries/devices/useGetUnitControllers';
import useParentDeviceFieldsVisibility from '@src/hooks/api/queries/devices/useParentDeviceFieldsVisibility';
import {
  BulkCreateDevicesDto,
  ParentSourceDeviceEntry,
  UnitControllerEntry,
} from '@src/types/Device';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CreateMultipleDevicesFormProps } from './CreateMultipleDevicesForm.types';

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 100;

const CONTROLLER_TYPES = new Set(['unitController', 'siteController']);
const NONE_PARENT = '__none__';

interface FormValues {
  type: string;
  parentControllerId: string;
  namePrefix: string;
}

export default function useCreateMultipleDevicesForm({
  setOpen,
  setConfirmOpen,
  setErrorMessage,
}: CreateMultipleDevicesFormProps) {
  const { control, handleSubmit, watch, setValue, reset } = useForm<FormValues>(
    {
      defaultValues: {
        type: '',
        parentControllerId: '',
        namePrefix: '',
      },
      mode: 'onChange',
    },
  );

  const deviceType = watch('type');
  const parentControllerId = watch('parentControllerId');

  const { data: deviceTypesData } = useGetDeviceTypes();
  const deviceTypes = useMemo(
    () => deviceTypesData?.deviceTypes ?? [],
    [deviceTypesData],
  );

  const isCustomDeviceType = useMemo(
    () => !!deviceType && !deviceTypes.some((type) => type.name === deviceType),
    [deviceType, deviceTypes],
  );

  // Validate device type: only letters allowed (no numbers or special characters)
  const deviceTypeError = useMemo(() => {
    if (!deviceType) {
      return '';
    }
    if (!/^[a-zA-Z]+$/.test(deviceType)) {
      return 'Device type must contain only letters (no numbers or special characters)';
    }
    return '';
  }, [deviceType]);

  const isStandardTypeSelected =
    deviceType !== '' && deviceType !== undefined && !isCustomDeviceType;

  const { data: parentDevices } = useGetParentDevices({
    deviceType,
    options: { enabled: isStandardTypeSelected },
    queryKeys: [deviceType],
  });

  const { data: parentControllers } = useGetDevicesParentControllers({
    deviceType: deviceType || '',
    options: { enabled: isStandardTypeSelected },
    queryKeys: [deviceType],
  });

  // Fetch all unit controllers directly (not filtered by existing child devices)
  const { data: unitControllersData } = useGetUnitControllers({
    enabled: isStandardTypeSelected && !isCustomDeviceType,
  });

  const {
    hideParentSourceDevice,
    hideParentController,
    showBothParentsAndControllers,
    showParentControllerInsteadOfSource,
    sortedParentDeviceIds,
    controllerOrParentOptions: visibilityControllerOrParentOptions,
  } = useParentDeviceFieldsVisibility({
    deviceType: deviceType || '',
    deviceTypes,
    parentDevices,
    parentControllers,
  });

  // Determine if the selected device type is a unit controller type
  const isUnitControllerMode = useMemo(() => {
    if (!deviceType) {
      return false;
    }
    return deviceType === 'unitController';
  }, [deviceType]);

  // For source device mode: determine cascading parent source device levels
  // We check the parent devices' types - if they are controllers, it's level 1 source device
  // If they are source devices, we need to cascade up
  const parentSourceDeviceTypes = useMemo(() => {
    const devices = parentDevices?.devices;
    if (!devices || !Array.isArray(devices)) {
      return new Set<string>();
    }
    return new Set(devices.map((d) => d.type));
  }, [parentDevices]);

  // Check if parent source devices are controllers (meaning we're at sourceDeviceLevel1)
  const parentIsController = useMemo(() => {
    if (!parentDevices?.devices) {
      return false;
    }
    return parentDevices.devices.some((d) => CONTROLLER_TYPES.has(d.type));
  }, [parentDevices]);

  // Closest parent type: the non-controller type from parentSourceDeviceTypes
  const closestParentType = useMemo(() => {
    const types = [...parentSourceDeviceTypes];
    return types.find((t) => !CONTROLLER_TYPES.has(t)) || '';
  }, [parentSourceDeviceTypes]);

  // For cascading: fetch parents of the closest parent type to discover hierarchy
  const { data: grandparentDevices } = useGetParentDevices({
    deviceType: closestParentType,
    options: { enabled: !!closestParentType },
    queryKeys: [closestParentType],
  });

  // Check if we need a third level of cascading (sourceDeviceLevel3)
  const grandparentSourceDeviceTypes = useMemo(() => {
    const devices = grandparentDevices?.devices;
    if (!devices || !Array.isArray(devices)) {
      return new Set<string>();
    }
    return new Set(devices.map((d) => d.type));
  }, [grandparentDevices]);

  const secondParentType = useMemo(() => {
    const types = [...grandparentSourceDeviceTypes];
    return types.length > 0 && !CONTROLLER_TYPES.has(types[0]) ? types[0] : '';
  }, [grandparentSourceDeviceTypes]);

  const { data: greatGrandparentDevices } = useGetParentDevices({
    deviceType: secondParentType,
    options: { enabled: !!secondParentType },
    queryKeys: [secondParentType],
  });

  // Fetch ALL devices of each parent type (not just those with existing children)
  const { data: allClosestParentDevices } = useGetAllDevicesByType({
    deviceType: closestParentType,
    options: { enabled: !!closestParentType && !parentIsController },
    queryKeys: [closestParentType],
  });

  const { data: allSecondParentDevices } = useGetAllDevicesByType({
    deviceType: secondParentType,
    options: { enabled: !!secondParentType },
    queryKeys: [secondParentType],
  });

  // --- Source device mode: ancestor selections (declared before cascadingLevels which depends on them) ---
  const [ancestorSelections, setAncestorSelections] = useState<
    Record<number, string>
  >({});

  // Build the cascading levels for source device creation
  // Each level has: type, available devices, selected device(s)
  // We use /devices/parents for hierarchy discovery but /devices/device-ids for complete device lists
  // Ancestor levels are filtered by selected controller and by prior ancestor selections
  const cascadingLevels = useMemo(() => {
    const levels: Array<{
      type: string;
      devices: Array<{
        deviceId: string;
        name: string;
        type: string;
        parentControllerId?: string;
        parentSourceDeviceId?: string;
      }>;
      isClosestParent: boolean;
    }> = [];

    if (isUnitControllerMode || !deviceType) {
      return levels;
    }

    // Use hierarchy discovery from /devices/parents to determine structure
    if (parentDevices?.devices && !parentIsController) {
      if (closestParentType && grandparentDevices?.devices) {
        const grandparentIsController = grandparentDevices.devices.some((d) =>
          CONTROLLER_TYPES.has(d.type),
        );

        if (
          !grandparentIsController &&
          secondParentType &&
          greatGrandparentDevices?.devices
        ) {
          // sourceDeviceLevel3: ancestor (secondParentType) + closest parent
          // Ancestor: filter by selected controller
          let secondParentFiltered = (
            allSecondParentDevices?.devices ?? []
          ).filter((d) => !CONTROLLER_TYPES.has(d.type));
          if (parentControllerId) {
            secondParentFiltered = secondParentFiltered.filter(
              (d) => d.parentControllerId === parentControllerId,
            );
          }
          levels.push({
            type: secondParentType,
            devices: secondParentFiltered,
            isClosestParent: false,
          });
        }
        // sourceDeviceLevel2: no ancestor levels needed, just the closest parent below
      }

      // The closest parent level (allows multi-select + quantities)
      levels.push({
        type: closestParentType,
        devices: (allClosestParentDevices?.devices ?? []).filter(
          (d) => !CONTROLLER_TYPES.has(d.type),
        ),
        isClosestParent: true,
      });
    }

    return levels;
  }, [
    isUnitControllerMode,
    deviceType,
    parentDevices,
    parentIsController,
    closestParentType,
    grandparentDevices,
    secondParentType,
    greatGrandparentDevices,
    allClosestParentDevices,
    allSecondParentDevices,
    parentControllerId,
  ]);

  // --- Unit controller mode: dynamic groups based on quantity ---
  const [unitControllerQuantity, setUnitControllerQuantity] =
    useState(MIN_QUANTITY);
  const [unitControllerEntries, setUnitControllerEntries] = useState<
    UnitControllerEntry[]
  >([{ id: crypto.randomUUID(), name: '', engineeringId: '' }]);

  const handleUnitControllerQuantityChange = useCallback(
    (newQuantity: number) => {
      const clamped = Math.max(
        MIN_QUANTITY,
        Math.min(MAX_QUANTITY, newQuantity),
      );
      setUnitControllerQuantity(clamped);
      setUnitControllerEntries((prev) => {
        if (clamped > prev.length) {
          return [
            ...prev,
            ...Array.from({ length: clamped - prev.length }, () => ({
              id: crypto.randomUUID(),
              name: '',
              engineeringId: '',
            })),
          ];
        }
        return prev.slice(0, clamped);
      });
    },
    [],
  );

  const handleUnitControllerEntryChange = useCallback(
    (index: number, field: keyof UnitControllerEntry, value: string) => {
      setUnitControllerEntries((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    [],
  );

  // --- Source device mode: per-parent quantities ---
  const [parentSourceDeviceEntries, setParentSourceDeviceEntries] = useState<
    ParentSourceDeviceEntry[]
  >([]);

  // --- Custom device type mode: locked parent type + all devices ---
  const [lockedCustomParentType, setLockedCustomParentType] = useState<
    string | null
  >(null);

  const { data: allDevicesData } = useGetDevices();

  // Build controller options for custom device type
  const customControllerOptions = useMemo(() => {
    if (!isCustomDeviceType || !allDevicesData) {
      return [];
    }
    return allDevicesData
      .filter((d) => CONTROLLER_TYPES.has(d.type) && !d.parentSourceDeviceId)
      .map((d) => ({ deviceId: d.deviceId, name: d.name, type: d.type }))
      .sort((a, b) => {
        // siteController always first
        if (a.type === 'siteController' && b.type !== 'siteController') {
          return -1;
        }
        if (b.type === 'siteController' && a.type !== 'siteController') {
          return 1;
        }
        const numA = a.deviceId.match(/_(\d+)$/);
        const numB = b.deviceId.match(/_(\d+)$/);
        if (numA && numB) {
          return parseInt(numA[1], 10) - parseInt(numB[1], 10);
        }
        return a.deviceId.localeCompare(b.deviceId);
      });
  }, [isCustomDeviceType, allDevicesData]);

  // Determine sourceDeviceLevel3 device IDs to exclude
  // A device is level3 if its parentSourceDeviceId points to a device
  // whose own parentSourceDeviceId is also non-null
  const sourceDeviceLevel3Ids = useMemo(() => {
    if (!allDevicesData) {
      return new Set<string>();
    }
    const parentSourceMap = new Map<string, string | null>();
    for (const d of allDevicesData) {
      parentSourceMap.set(d.deviceId, d.parentSourceDeviceId);
    }
    const level3Ids = new Set<string>();
    for (const d of allDevicesData) {
      if (CONTROLLER_TYPES.has(d.type)) {
        continue;
      }
      // Check if this device's parent source device also has a parent source device
      const parentId = d.parentSourceDeviceId;
      if (parentId) {
        const grandparentId = parentSourceMap.get(parentId);
        if (grandparentId) {
          // This device is level 3 (its parent is level 2)
          level3Ids.add(d.deviceId);
        }
      }
    }
    return level3Ids;
  }, [allDevicesData]);

  // Filtered parent source devices for custom device type
  // Excludes controllers, level3 devices, and locks to selected type
  const customParentDeviceOptions = useMemo(() => {
    if (!isCustomDeviceType || !allDevicesData || !parentControllerId) {
      return [];
    }
    let devices = allDevicesData.filter(
      (d) =>
        !CONTROLLER_TYPES.has(d.type) &&
        d.parentControllerId === parentControllerId &&
        !sourceDeviceLevel3Ids.has(d.deviceId),
    );
    // If a type is locked, restrict to that type
    if (lockedCustomParentType) {
      devices = devices.filter((d) => d.type === lockedCustomParentType);
    }
    return devices
      .map((d) => ({
        deviceId: d.deviceId,
        name: d.name,
        type: d.type,
        parentSourceDeviceId: d.parentSourceDeviceId,
      }))
      .sort((a, b) => {
        const numA = a.deviceId.match(/_(\d+)$/);
        const numB = b.deviceId.match(/_(\d+)$/);
        if (numA && numB) {
          return parseInt(numA[1], 10) - parseInt(numB[1], 10);
        }
        return a.deviceId.localeCompare(b.deviceId);
      });
  }, [
    isCustomDeviceType,
    allDevicesData,
    parentControllerId,
    sourceDeviceLevel3Ids,
    lockedCustomParentType,
  ]);

  const handleAncestorSelect = useCallback(
    (levelIndex: number, deviceId: string) => {
      setAncestorSelections((prev) => ({ ...prev, [levelIndex]: deviceId }));
    },
    [],
  );

  const handleAddParentSourceDevice = useCallback(
    (parentSourceDeviceId: string) => {
      // NONE means no parent source device — create directly under controller
      if (parentSourceDeviceId === NONE_PARENT) {
        setLockedCustomParentType(NONE_PARENT);
        setParentSourceDeviceEntries([
          { parentSourceDeviceId: NONE_PARENT, quantity: 1 },
        ]);
        return;
      }
      // For custom device type, lock to the type of the first selected device
      if (isCustomDeviceType && allDevicesData) {
        const device = allDevicesData.find(
          (d) => d.deviceId === parentSourceDeviceId,
        );
        if (device && !lockedCustomParentType) {
          setLockedCustomParentType(device.type);
        }
      }
      setParentSourceDeviceEntries((prev) => {
        if (prev.some((e) => e.parentSourceDeviceId === parentSourceDeviceId)) {
          return prev;
        }
        return [...prev, { parentSourceDeviceId, quantity: 1 }];
      });
    },
    [isCustomDeviceType, allDevicesData, lockedCustomParentType],
  );

  const handleRemoveParentSourceDevice = useCallback(
    (parentSourceDeviceId: string) => {
      setParentSourceDeviceEntries((prev) => {
        const updated = prev.filter(
          (e) => e.parentSourceDeviceId !== parentSourceDeviceId,
        );
        // If all removed, unlock the type
        if (updated.length === 0) {
          setLockedCustomParentType(null);
        }
        return updated;
      });
    },
    [],
  );

  const handleParentSourceDeviceQuantityChange = useCallback(
    (parentSourceDeviceId: string, quantity: number) => {
      setParentSourceDeviceEntries((prev) =>
        prev.map((e) =>
          e.parentSourceDeviceId === parentSourceDeviceId
            ? {
                ...e,
                quantity: Math.max(
                  MIN_QUANTITY,
                  Math.min(MAX_QUANTITY, quantity),
                ),
              }
            : e,
        ),
      );
    },
    [],
  );

  // Build full device path for nested devices (e.g., "POWINEMS 1 -> POWINBESS 2")
  const getFullDevicePath = useCallback(
    (deviceId: string): string => {
      if (deviceId === NONE_PARENT) {
        return 'NONE';
      }
      const getName = (id: string): string => {
        if (allDevicesData) {
          const device = allDevicesData.find((d) => d.deviceId === id);
          if (device) {
            return device.name || id;
          }
        }
        return id;
      };
      const parts: string[] = [];
      let currentId: string | null = deviceId;
      while (currentId) {
        const device = allDevicesData?.find((d) => d.deviceId === currentId);
        if (!device) {
          parts.unshift(getName(currentId));
          break;
        }
        parts.unshift(device.name || currentId);
        // Walk up through parent source devices (not controllers)
        if (device.parentSourceDeviceId && !CONTROLLER_TYPES.has(device.type)) {
          currentId = device.parentSourceDeviceId;
        } else {
          break;
        }
      }
      return parts.join(' → ');
    },
    [allDevicesData],
  );

  // Name maps - merge names from all device-by-type hooks for complete coverage
  const parentDeviceNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of parentDevices?.devices ?? []) {
      map.set(d.deviceId, d.name);
    }
    for (const d of allClosestParentDevices?.devices ?? []) {
      map.set(d.deviceId, d.name);
    }
    for (const d of allSecondParentDevices?.devices ?? []) {
      map.set(d.deviceId, d.name);
    }
    // Include all devices for custom device type name resolution
    for (const d of allDevicesData ?? []) {
      if (!map.has(d.deviceId)) {
        map.set(d.deviceId, d.name);
      }
    }
    return map;
  }, [
    parentDevices,
    allClosestParentDevices,
    allSecondParentDevices,
    allDevicesData,
  ]);

  const controllerNameMap = useMemo(
    () =>
      new Map(
        parentControllers?.devices?.map((d) => [d.deviceId, d.name] as const) ??
          [],
      ),
    [parentControllers],
  );

  // Name map for all unit controllers (used in source device mode)
  const unitControllerNameMap = useMemo(
    () =>
      new Map(
        unitControllersData?.devices?.map(
          (d) => [d.deviceId, d.name] as const,
        ) ?? [],
      ),
    [unitControllersData],
  );

  // Sorted unit controller IDs for the dropdown
  const unitControllerOptions = useMemo(() => {
    const devices = unitControllersData?.devices;
    if (!devices || !Array.isArray(devices)) {
      return [];
    }
    return devices
      .map((d) => d.deviceId)
      .sort((a, b) => {
        const numA = a.match(/_(\d+)$/);
        const numB = b.match(/_(\d+)$/);
        if (numA && numB) {
          return parseInt(numA[1], 10) - parseInt(numB[1], 10);
        }
        return a.localeCompare(b);
      });
  }, [unitControllersData]);

  const controllerOrParentNameMap = useMemo(() => {
    // In source device mode, use unit controllers fetched directly
    if (
      !isUnitControllerMode &&
      !isCustomDeviceType &&
      unitControllerNameMap.size > 0
    ) {
      return unitControllerNameMap;
    }
    if (showBothParentsAndControllers || showParentControllerInsteadOfSource) {
      return controllerNameMap;
    }
    return hideParentSourceDevice ? parentDeviceNameMap : controllerNameMap;
  }, [
    isUnitControllerMode,
    isCustomDeviceType,
    unitControllerNameMap,
    showBothParentsAndControllers,
    showParentControllerInsteadOfSource,
    hideParentSourceDevice,
    controllerNameMap,
    parentDeviceNameMap,
  ]);

  // Filtered device types (exclude siteController, also exclude sourceDeviceLevel3 types for new device creation)
  const filteredDeviceTypes = useMemo(
    () =>
      deviceTypes.reduce<typeof deviceTypes>((acc, type) => {
        if (
          type &&
          typeof type.name === 'string' &&
          type.name.trim() !== '' &&
          type.name !== 'siteController'
        ) {
          acc.push(type);
        }
        return acc;
      }, []),
    [deviceTypes],
  );

  // Auto-select parent controller: for unit controllers always use siteController_1,
  // otherwise auto-select when only one is available
  useEffect(() => {
    if (isUnitControllerMode) {
      if (!parentControllerId) {
        setValue('parentControllerId', 'siteController_1');
      }
      return;
    }
    const controllerDevices = hideParentSourceDevice
      ? parentDevices?.devices
      : parentControllers?.devices;
    if (
      controllerDevices &&
      Array.isArray(controllerDevices) &&
      controllerDevices.length === 1 &&
      !parentControllerId
    ) {
      setValue('parentControllerId', controllerDevices[0].deviceId);
    }
  }, [
    parentControllers,
    parentDevices,
    hideParentSourceDevice,
    parentControllerId,
    isUnitControllerMode,
    setValue,
  ]);

  const resetParentFields = useCallback(() => {
    setValue('parentControllerId', '');
    setAncestorSelections({});
    setParentSourceDeviceEntries([]);
    setLockedCustomParentType(null);
  }, [setValue]);

  // Clear stale parent source entries when the parent controller changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run when parentControllerId changes
  useEffect(() => {
    setParentSourceDeviceEntries([]);
    setAncestorSelections({});
  }, [parentControllerId]);

  const resetFormState = useCallback(() => {
    reset();
    setUnitControllerQuantity(MIN_QUANTITY);
    setUnitControllerEntries([
      { id: crypto.randomUUID(), name: '', engineeringId: '' },
    ]);
    setAncestorSelections({});
    setParentSourceDeviceEntries([]);
    setLockedCustomParentType(null);
  }, [reset]);

  const handleCancel = useCallback(() => {
    resetFormState();
    setOpen(false);
  }, [resetFormState, setOpen]);

  const { createBulkDevices } = useCreateBulkDevices();
  const queryClient = useQueryClient();

  const handleCreateDevices = useCallback(() => {
    const currentType = deviceType;
    const currentControllerId = parentControllerId;

    const mutationOptions = {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.devices],
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey.includes(QueryKeys.devicesParents),
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.parentControllers],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.deviceTypes],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.unitControllers],
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey.includes(QueryKeys.allDevicesByType),
        });
        setConfirmOpen(true);
        setOpen(false);
      },
      onError: (error: Error) => {
        const customError = error.cause as {
          message?: string;
          error?: { message?: string };
        };
        const message =
          customError?.error?.message ??
          customError?.message ??
          'Failed to create devices.';
        setErrorMessage(message);
      },
    };

    if (isUnitControllerMode) {
      const payload: BulkCreateDevicesDto = {
        mode: 'unitController',
        type: currentType,
        parentControllerId: currentControllerId || undefined,
        unitControllers: unitControllerEntries.map(
          ({ name, engineeringId }) => ({ name, engineeringId }),
        ),
      };
      createBulkDevices.mutate(payload, mutationOptions);
    } else {
      const namePrefix = watch('namePrefix');
      const ancestorIds = Object.values(ancestorSelections).filter(Boolean);

      // Custom device type with NONE parent: create directly under controller
      const isNoneParent =
        isCustomDeviceType &&
        parentSourceDeviceEntries.some(
          (e) => e.parentSourceDeviceId === NONE_PARENT,
        );

      const effectiveParentIsController = parentIsController || isNoneParent;

      const effectiveEntries = isNoneParent
        ? [
            {
              parentSourceDeviceId: currentControllerId,
              quantity: parentSourceDeviceEntries[0]?.quantity ?? 1,
            },
          ]
        : parentSourceDeviceEntries;

      const payload: BulkCreateDevicesDto = {
        mode: 'sourceDevice',
        type: currentType,
        parentControllerId: effectiveParentIsController
          ? undefined
          : currentControllerId || undefined,
        namePrefix: namePrefix || undefined,
        parentSourceDevices: effectiveEntries,
        ancestorSourceDeviceIds:
          ancestorIds.length > 0 ? ancestorIds : undefined,
        parentIsController: effectiveParentIsController || undefined,
      };
      createBulkDevices.mutate(payload, mutationOptions);
    }
  }, [
    deviceType,
    parentControllerId,
    isUnitControllerMode,
    isCustomDeviceType,
    parentIsController,
    unitControllerEntries,
    parentSourceDeviceEntries,
    ancestorSelections,
    watch,
    createBulkDevices,
    queryClient,
    setConfirmOpen,
    setErrorMessage,
    setOpen,
  ]);

  const isCreateButtonEnabled = useMemo(() => {
    if (!deviceType || deviceTypeError) {
      return false;
    }

    const hasParentController = Boolean(parentControllerId);

    if (isUnitControllerMode) {
      if (!hasParentController && !hideParentController) {
        return false;
      }
      return unitControllerEntries.every(
        (entry) =>
          entry.name.trim() !== '' && entry.engineeringId.trim() !== '',
      );
    }

    // Custom device type mode
    if (isCustomDeviceType) {
      if (!hasParentController) {
        return false;
      }
      if (parentSourceDeviceEntries.length === 0) {
        return false;
      }
      // NONE selection only needs quantity check
      if (
        parentSourceDeviceEntries.some(
          (e) => e.parentSourceDeviceId === NONE_PARENT,
        )
      ) {
        return parentSourceDeviceEntries.every(
          (e) => e.quantity >= MIN_QUANTITY,
        );
      }
      return parentSourceDeviceEntries.every((e) => e.quantity >= MIN_QUANTITY);
    }

    // Source device mode
    if (parentIsController) {
      // Level 1: quantity per controller, no single parent controller needed
      if (parentSourceDeviceEntries.length === 0) {
        return false;
      }
      return parentSourceDeviceEntries.every((e) => e.quantity >= MIN_QUANTITY);
    }
    if (!hasParentController && !hideParentController) {
      return false;
    }
    if (parentSourceDeviceEntries.length === 0) {
      return false;
    }
    return parentSourceDeviceEntries.every((e) => e.quantity >= MIN_QUANTITY);
  }, [
    deviceType,
    deviceTypeError,
    parentControllerId,
    isUnitControllerMode,
    isCustomDeviceType,
    parentIsController,
    hideParentController,
    unitControllerEntries,
    parentSourceDeviceEntries,
  ]);

  const isParentControllerDisabled = useMemo(() => !deviceType, [deviceType]);

  // Available closest parent devices filtered by selected controller/ancestor
  // For parentIsController (level 1), this returns controller devices
  const filteredClosestParentDevices = useMemo(() => {
    const sortAlphanumerically = (
      devices: Array<{ deviceId: string; name: string; type: string }>,
    ) =>
      [...devices].sort((a, b) => {
        const numA = a.deviceId.match(/_(\d+)$/);
        const numB = b.deviceId.match(/_(\d+)$/);
        if (numA && numB) {
          return parseInt(numA[1], 10) - parseInt(numB[1], 10);
        }
        return a.deviceId.localeCompare(b.deviceId);
      });

    if (parentIsController) {
      // Level 1: controllers are the closest parent
      const controllers = unitControllersData?.devices;
      if (!controllers || !Array.isArray(controllers)) {
        return [];
      }
      return sortAlphanumerically(
        controllers.map((d) => ({
          deviceId: d.deviceId,
          name: d.name,
          type: d.type,
        })),
      );
    }

    const closestLevel = cascadingLevels.find((l) => l.isClosestParent);
    if (!closestLevel) {
      return [];
    }

    let filtered = closestLevel.devices;

    // Filter by selected controller
    if (parentControllerId) {
      filtered = filtered.filter(
        (d) =>
          !('parentControllerId' in d) ||
          d.parentControllerId === parentControllerId,
      );
    }

    // If there are ancestor levels, filter by the selected ancestor at the level just above
    const ancestorLevels = cascadingLevels.filter((l) => !l.isClosestParent);
    if (ancestorLevels.length > 0) {
      const lastAncestorIndex = ancestorLevels.length - 1;
      const selectedAncestor = ancestorSelections[lastAncestorIndex];
      if (selectedAncestor) {
        // Filter closest parent devices by parentSourceDeviceId matching selected ancestor
        filtered = filtered.filter(
          (d) =>
            ('parentSourceDeviceId' in d &&
              d.parentSourceDeviceId === selectedAncestor) ||
            d.deviceId.startsWith(selectedAncestor),
        );
      }
    }

    return sortAlphanumerically(filtered);
  }, [
    parentIsController,
    unitControllersData,
    cascadingLevels,
    ancestorSelections,
    parentControllerId,
  ]);

  return {
    control,
    handleSubmit,
    deviceType,
    isCustomDeviceType,
    filteredDeviceTypes,
    isUnitControllerMode,
    parentIsController,
    showParentSourceDeviceField:
      showBothParentsAndControllers || !showParentControllerInsteadOfSource,
    showParentControllerField:
      showBothParentsAndControllers ||
      showParentControllerInsteadOfSource ||
      !hideParentController,
    isParentControllerDisabled,
    parentSourceDeviceOptions: sortedParentDeviceIds,
    controllerOrParentOptions:
      !isUnitControllerMode &&
      !isCustomDeviceType &&
      unitControllerOptions.length > 0
        ? unitControllerOptions
        : visibilityControllerOrParentOptions,
    parentDeviceNameMap,
    controllerOrParentNameMap,
    parentControllerIdFallback: parentControllerId || '',
    resetParentFields,
    handleCancel,
    isCreateButtonEnabled,
    deviceTypeError,
    // Unit controller mode
    unitControllerQuantity,
    unitControllerEntries,
    handleUnitControllerQuantityChange,
    handleUnitControllerEntryChange,
    minQuantity: MIN_QUANTITY,
    maxQuantity: MAX_QUANTITY,
    // Source device mode
    cascadingLevels,
    ancestorSelections,
    handleAncestorSelect,
    parentSourceDeviceEntries,
    handleAddParentSourceDevice,
    handleRemoveParentSourceDevice,
    handleParentSourceDeviceQuantityChange,
    filteredClosestParentDevices,
    // Custom device type mode
    customControllerOptions,
    customParentDeviceOptions,
    lockedCustomParentType,
    getFullDevicePath,
    noneParent: NONE_PARENT,
    // Actions
    handleCreateDevices,
    handleReset: resetFormState,
  };
}
